"use client"

/**
 * The photo and video viewer — the owner's pick "A" (2026-10-18): dark, full screen, a thumbnail
 * strip. The files of one note, comment or visit, in order: swipe, the arrows or ← →; pinch,
 * double-tap, double-click or the wheel zoom a photo and a drag pans it; a swipe down, Esc, ✕ or
 * the phone's Back button closes. The same design as the dashboard's viewer.
 *
 * A nested Radix dialog on purpose: opened from the appointment window (itself a modal dialog), a
 * plain overlay would sit outside its focus trap and its outside-click guard.
 */
import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { ChevronLeft, ChevronRight, Download, Loader2, Play, Trash2, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Dialog, DialogPortal } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import type { AttachmentItem } from "./types"

interface AttachmentViewerProps {
  items: AttachmentItem[]
  /** The open file; null when closed */
  index: number | null
  onIndexChange: (index: number | null) => void
  /** Shown only when the viewer may delete (rights) */
  onDelete?: (item: AttachmentItem) => void
}

type Zoom = { s: number; x: number; y: number }

export function AttachmentViewer({ items, index, onIndexChange, onDelete }: AttachmentViewerProps) {
  const { t } = useTranslation()
  const open = index !== null && items.length > 0
  const idx = open ? Math.min(Math.max(index!, 0), items.length - 1) : 0
  const item = open ? items[idx] : null

  const stageRef = React.useRef<HTMLDivElement>(null)
  const imgRef = React.useRef<HTMLImageElement>(null)
  const stripRef = React.useRef<HTMLDivElement>(null)
  const zoom = React.useRef<Zoom>({ s: 1, x: 0, y: 0 })
  const pointers = React.useRef(new Map<number, { x: number; y: number }>())
  // onBg: the press began on the dark area itself (pointer capture retargets the later events to the stage)
  const gesture = React.useRef<{ x: number; y: number; zx: number; zy: number; t: number; onBg: boolean } | null>(null)
  const pinch = React.useRef<{ d: number; s: number } | null>(null)
  const lastTap = React.useRef(0)
  const pushed = React.useRef(false)

  const close = React.useCallback(() => onIndexChange(null), [onIndexChange])
  const go = React.useCallback((step: number) => {
    if (items.length < 2) return
    onIndexChange((idx + step + items.length) % items.length)
  }, [idx, items.length, onIndexChange])

  // the phone's Back button closes the viewer instead of leaving the page
  React.useEffect(() => {
    if (!open) return
    if (!pushed.current) {
      window.history.pushState({ attachmentViewer: true }, "")
      pushed.current = true
    }
    const onPop = () => { pushed.current = false; close() }
    window.addEventListener("popstate", onPop)
    return () => window.removeEventListener("popstate", onPop)
  }, [open, close])
  React.useEffect(() => {
    if (!open && pushed.current) { pushed.current = false; window.history.back() }
  }, [open])

  // a new file: no zoom, the strip follows, the neighbours load ahead
  React.useEffect(() => {
    if (!open) return
    zoom.current = { s: 1, x: 0, y: 0 }
    if (imgRef.current) apply(false)
    stripRef.current?.querySelector<HTMLElement>("[data-on='true']")?.scrollIntoView({ block: "nearest", inline: "center" })
    for (const k of [idx + 1, idx - 1]) {
      const n = items[(k + items.length) % items.length]
      if (n && n.kind === "photo" && !n.processing) { const im = new Image(); im.src = n.src }
    }
  }, [open, idx, items])

  function apply(animate: boolean) {
    const img = imgRef.current
    if (!img) return
    const z = zoom.current
    img.style.transition = animate ? "transform .22s ease" : "none"
    img.style.transform = `translate(${z.x}px, ${z.y}px) scale(${z.s})`
    img.style.cursor = z.s > 1 ? "grab" : "zoom-in"
  }
  function zoomAt(scale: number, cx: number, cy: number) {
    const img = imgRef.current
    if (!img) return
    const z = zoom.current
    const ns = Math.min(4, Math.max(1, scale))
    if (ns === 1) { zoom.current = { s: 1, x: 0, y: 0 }; apply(true); return }
    const r = img.getBoundingClientRect()
    const px = (cx - r.left) / z.s, py = (cy - r.top) / z.s
    zoom.current = { s: ns, x: z.x + px * z.s - px * ns, y: z.y + py * z.s - py * ns }
    apply(true)
  }

  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).tagName === "VIDEO") return
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    stageRef.current?.setPointerCapture(e.pointerId)
    if (pointers.current.size === 1) gesture.current = { x: e.clientX, y: e.clientY, zx: zoom.current.x, zy: zoom.current.y,
                                                         t: Date.now(), onBg: e.target === stageRef.current }
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()]
      pinch.current = { d: Math.hypot(a.x - b.x, a.y - b.y), s: zoom.current.s }
    }
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    if (pinch.current && pointers.current.size === 2 && imgRef.current) {
      const [a, b] = [...pointers.current.values()]
      zoom.current.s = Math.min(4, Math.max(1, pinch.current.s * Math.hypot(a.x - b.x, a.y - b.y) / pinch.current.d))
      apply(false)
    } else if (gesture.current && imgRef.current && zoom.current.s > 1) {
      zoom.current.x = gesture.current.zx + e.clientX - gesture.current.x
      zoom.current.y = gesture.current.zy + e.clientY - gesture.current.y
      apply(false)
    }
  }
  const onPointerUp = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return
    pointers.current.delete(e.pointerId)
    if (pinch.current) {
      if (pointers.current.size < 2) { pinch.current = null; if (zoom.current.s <= 1.02) { zoom.current = { s: 1, x: 0, y: 0 }; apply(true) } }
      gesture.current = null
      return
    }
    const g = gesture.current
    gesture.current = null
    if (!g) return
    const dx = e.clientX - g.x, dy = e.clientY - g.y
    const tap = Date.now() - g.t < 260 && Math.abs(dx) < 8 && Math.abs(dy) < 8
    if (zoom.current.s <= 1) {
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1)
      else if (dy > 110 && Math.abs(dy) > Math.abs(dx)) close()
      else if (tap && e.pointerType === "touch") {
        if (Date.now() - lastTap.current < 300) { zoomAt(2.5, e.clientX, e.clientY); lastTap.current = 0 }
        else lastTap.current = Date.now()
      } else if (tap && g.onBg && e.pointerType !== "touch") close() // a click on the dark area
    } else if (tap && e.pointerType === "touch") {
      if (Date.now() - lastTap.current < 300) { zoom.current = { s: 1, x: 0, y: 0 }; apply(true); lastTap.current = 0 }
      else lastTap.current = Date.now()
    }
  }

  const label = item?.kind === "video" ? t("attachments.video", "Video") : t("attachments.photo", "Photo")

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close() }}>
      <DialogPortal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-[#0c0e11]/95" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-[60] flex flex-col text-white outline-none"
          aria-describedby={undefined}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") { e.preventDefault(); go(1) }
            else if (e.key === "ArrowLeft") { e.preventDefault(); go(-1) }
          }}
        >
          <DialogPrimitive.Title className="sr-only">{t("attachments.viewer_title", "Photos and videos")}</DialogPrimitive.Title>
          {item && (
            <>
              <div className="z-[2] flex items-center gap-3.5 py-2.5 pl-4 pr-3 text-[13px] text-white/80">
                <span className="font-semibold tabular-nums text-white">{idx + 1} / {items.length}</span>
                <span className="truncate"><b className="font-semibold text-white">{item.by}</b> · {item.when}</span>
                <span className="ml-auto flex gap-0.5">
                  {!item.processing && !item.failed && (
                    <a href={item.downloadSrc || item.src} download className="grid h-10 w-10 place-items-center rounded-full text-white hover:bg-white/10"
                       title={t("attachments.download", "Download")}><Download className="h-[18px] w-[18px]" /></a>
                  )}
                  {onDelete && (
                    <button type="button" onClick={() => onDelete(item)} className="grid h-10 w-10 place-items-center rounded-full hover:bg-white/10"
                            title={t("attachments.delete", "Delete")}><Trash2 className="h-[18px] w-[18px]" /></button>
                  )}
                  <DialogPrimitive.Close className="grid h-10 w-10 place-items-center rounded-full hover:bg-white/10"
                                         title={t("attachments.close", "Close (Esc)")}><X className="h-5 w-5" /></DialogPrimitive.Close>
                </span>
              </div>

              <div
                ref={stageRef}
                className="relative min-h-0 flex-1 touch-none select-none overflow-hidden"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onDoubleClick={(e) => {
                  if (!imgRef.current) return
                  if (zoom.current.s > 1) { zoom.current = { s: 1, x: 0, y: 0 }; apply(true) } else zoomAt(2.5, e.clientX, e.clientY)
                }}
                onWheel={(e) => { if (imgRef.current) zoomAt(zoom.current.s * (e.deltaY < 0 ? 1.18 : 1 / 1.18), e.clientX, e.clientY) }}
              >
                {item.failed ? (
                  <div className="absolute inset-0 grid place-content-center justify-items-center gap-2 px-6 text-center text-sm text-white/80">
                    <b className="text-white">{t("attachments.failed", "This video couldn't be prepared")}</b>
                    {t("attachments.failed_hint", "Delete it and send it again, or try another file.")}
                  </div>
                ) : item.processing ? (
                  <div className="absolute inset-0 grid place-content-center justify-items-center gap-3 text-center text-sm text-white/80">
                    <Loader2 className="h-7 w-7 animate-spin" />
                    <div><b className="text-white">{t("attachments.preparing", "Preparing this video")}</b><br />
                      {t("attachments.preparing_hint", "It will play here in about a minute.")}</div>
                  </div>
                ) : item.kind === "video" ? (
                  <video key={item.id} src={item.src} poster={item.thumb} controls playsInline preload="metadata"
                         className="absolute inset-0 m-auto max-h-[calc(100%-8px)] max-w-full rounded-md bg-black md:max-w-[calc(100%-140px)]" />
                ) : (
                  <img key={item.id} ref={imgRef} src={item.src} alt={label} draggable={false}
                       className="absolute inset-0 m-auto max-h-[calc(100%-8px)] max-w-full origin-top-left will-change-transform md:max-w-[calc(100%-140px)]" />
                )}
                {items.length > 1 && (
                  <>
                    <button type="button" onClick={() => go(-1)} aria-label={t("attachments.previous", "Previous")}
                            className="absolute left-4 top-1/2 z-[2] hidden h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/10 hover:bg-white/20 md:grid">
                      <ChevronLeft className="h-6 w-6" /></button>
                    <button type="button" onClick={() => go(1)} aria-label={t("attachments.next", "Next")}
                            className="absolute right-4 top-1/2 z-[2] hidden h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/10 hover:bg-white/20 md:grid">
                      <ChevronRight className="h-6 w-6" /></button>
                  </>
                )}
              </div>

              {items.length > 1 && (
                <div ref={stripRef} className="flex shrink-0 justify-center gap-1.5 overflow-x-auto px-3 pb-4 pt-2.5">
                  {items.map((it, k) => (
                    <button key={it.id} type="button" data-on={k === idx} onClick={() => onIndexChange(k)}
                            aria-label={`${k + 1}`}
                            className={cn("relative h-14 w-14 shrink-0 overflow-hidden rounded-lg border-2 bg-neutral-800 transition-opacity",
                                          k === idx ? "border-white opacity-100" : "border-transparent opacity-50 hover:opacity-80")}>
                      <img src={it.thumb} alt="" className="h-full w-full object-cover" />
                      {it.kind === "video" && <Play className="absolute bottom-1 left-1 h-3 w-3 fill-white text-white drop-shadow" />}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}
